import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import apis from '../../utils/apis';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import DateTimePicker from '../shared/TailwindComponents/DatePicker';
import CurrencyPicker from '../shared/TailwindComponents/CurrencyPicker';
import Button from '../shared/TailwindComponents/Button';
import { ymdToDate, dateToYmd } from '../Matters/economics/dateHelpers';
import type { FirmEconomicsFilters } from '../../types/economics';

interface FirmEconomicsFilterBarProps {
  value: FirmEconomicsFilters;
  onChange: (next: FirmEconomicsFilters) => void;
}

interface Person { user_id: string; first_name: string; last_name: string; email: string; }

/** Filter bar for the firm economics dashboard (US #2239, task #2279). */
const FirmEconomicsFilterBar: React.FC<FirmEconomicsFilterBarProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const people = useSelector((state: RootState) => (state.orgPeople?.people as Person[]) || []);

  const [clientOptions, setClientOptions] = useState<DropdownOption[]>([]);
  const [matterTypeOptions, setMatterTypeOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    dispatch(fetchOrgPeople());
    (async () => {
      try {
        const res = await apis.get('/clients', { params: { page: '1', page_size: '100' } });
        const clients = res?.data?.clients || [];
        setClientOptions(clients.map((c: any) => ({
          value: String(c.uuid || c.id || c.ClientID || ''),
          label: String(c.Name || c.name || 'Unknown'),
        })));
      } catch { /* leave empty */ }
      try {
        const res = await apis.get('/dropdown/matter-categories', { params: { active_only: true } });
        const cats = res?.data?.data || [];
        setMatterTypeOptions(cats.map((c: any) => ({
          value: String(c.id || c.category_id || ''),
          label: String(c.name || c.category_name || ''),
        })));
      } catch { /* leave empty */ }
    })();
  }, [dispatch]);

  const peopleOptions: DropdownOption[] = people.map((p) => ({
    value: String(p.user_id),
    label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email,
  }));

  const set = (patch: Partial<FirmEconomicsFilters>) => onChange({ ...value, ...patch });
  const asArray = (v: string | string[]) => (Array.isArray(v) ? v : v ? [v] : []);

  const hasAnyFilter =
    !!value.date_from || !!value.date_to || !!value.currency ||
    !!value.client_id?.length || !!value.matter_type_id?.length || !!value.user_id?.length;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <DateTimePicker
          id="firm-from"
          label={t('From')}
          selected={ymdToDate(value.date_from)}
          placeholder={t('From')}
          showTimeSelect={false}
          dateFormat="yyyy-MM-dd"
          fullWidth
          onChange={(d) => set({ date_from: dateToYmd(d) || undefined })}
        />
        <DateTimePicker
          id="firm-to"
          label={t('To')}
          selected={ymdToDate(value.date_to)}
          placeholder={t('To')}
          showTimeSelect={false}
          dateFormat="yyyy-MM-dd"
          fullWidth
          onChange={(d) => set({ date_to: dateToYmd(d) || undefined })}
        />
        <CurrencyPicker
          id="firm-currency"
          label={t('Currency')}
          value={value.currency}
          onChange={(currency) => set({ currency: currency || undefined })}
          className='w-full'
        />
        <Dropdown
          id="firm-clients"
          label={t('Clients')}
          multiple
          options={clientOptions}
          value={(value.client_id || []).map(String)}
          placeholder={t('All clients')}
          onChange={(v) => set({ client_id: asArray(v) })}
          fullWidth
        />
        <Dropdown
          id="firm-matter-types"
          label={t('Matter type')}
          multiple
          options={matterTypeOptions}
          value={(value.matter_type_id || []).map(String)}
          placeholder={t('All types')}
          onChange={(v) => set({ matter_type_id: asArray(v) })}
          fullWidth
        />
        <Dropdown
          id="firm-users"
          label={t('Users')}
          multiple
          options={peopleOptions}
          value={(value.user_id || []).map(String)}
          placeholder={t('All users')}
          onChange={(v) => set({ user_id: asArray(v) })}
          fullWidth
        />
      </div>
      {hasAnyFilter && (
        <div className="flex justify-end">
          <Button variant="neutral" size="small" onClick={() => onChange({})}>
            {t('Clear filters')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FirmEconomicsFilterBar;
